// vim: set ts=2 sts=2 sw=2 et:
//
// This file is part of OpenLifter, simple Powerlifting meet software.
// Copyright (C) 2019 The OpenPowerlifting Project.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as
// published by the Free Software Foundation, either version 3 of the
// License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.

// The parent component of the Weigh-ins page, contained by the WeighinsContainer.
// The Weigh-ins page updates more information in the Registration state.

import React from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";

import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Card from "react-bootstrap/Card";

import { getLiftersOnDay, isEntryPresent } from "../../logic/entry";
import { paginateEntries } from "../../logic/registration/paginateRegistrations";
import { getString } from "../../logic/strings";
import {
  emptyWeighinsFilters,
  hasStatusFilter,
  matchesWeighinsQuery,
  matchesWeighinsStatus,
  WeighinsFilters as WeighinsFiltersState,
} from "../../logic/weighins/filterWeighins";
import LifterTable from "./LifterTable";
import LifterRow from "./LifterRow";
import WeighinsFilters from "./WeighinsFilters";
import WrpfPullFromWebButton from "../../wrpf/handoff/WrpfPullFromWebButton";

import { Entry, Language } from "../../types/dataTypes";
import { GlobalState } from "../../types/stateTypes";

// For use when embedded inside the Lifting page.
interface OwnProps {
  day?: number;
  platform?: number;
  inLiftingPage?: boolean; // The weigh-ins page can be embedded.
}

interface StateProps {
  entries: ReadonlyArray<Entry>;
  paginationEnabled: boolean;
  language: Language;
}

type Props = Readonly<OwnProps> & Readonly<StateProps>;

interface InternalState {
  currentPage: number;
  filters: WeighinsFiltersState;
  // WRPF: ids matching the weigh-in/presence filters when they were chosen (or the search changed).
  // Frozen so a lifter who was just weighed in doesn't vanish from "Sin pesar" under the cursor.
  statusSnapshot: ReadonlySet<number> | null;
}

class WeighinsView extends React.Component<Props, InternalState> {
  constructor(props: Props) {
    super(props);
    this.getNumDaysFromEntries = this.getNumDaysFromEntries.bind(this);
    this.handlePreviousPage = this.handlePreviousPage.bind(this);
    this.handleNextPage = this.handleNextPage.bind(this);
    this.handleFiltersChange = this.handleFiltersChange.bind(this);
    this.handleFiltersClear = this.handleFiltersClear.bind(this);
    this.state = { currentPage: 0, filters: emptyWeighinsFilters(), statusSnapshot: null };
  }

  // Figure out how many days there are by looking at the entries themselves.
  //
  // The meet page information may be unreliable: someone might have created
  // a second day, added a lifter to it, then removed the second day without
  // yet updating that lifter.
  //
  // This is an attempt to make that error more obvious, so it can be corrected.
  getNumDaysFromEntries = () => {
    let max_day = 0;
    const entries = this.props.entries;
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      if (entry.day > max_day) {
        max_day = entry.day;
      }
    }
    return max_day;
  };

  isVisible = (entry: Entry): boolean => {
    const snapshot = this.state.statusSnapshot;
    if (snapshot !== null && !snapshot.has(entry.id)) return false;
    return matchesWeighinsQuery(entry, this.state.filters.query);
  };

  getOrderedEntries = (): Entry[] => {
    const orderedEntries: Entry[] = [];
    const numDays = this.getNumDaysFromEntries();

    for (let i = 1; i <= numDays; i++) {
      const lifters = getLiftersOnDay(this.props.entries, i).filter(this.isVisible);

      // Present the lifters in the same order as the unpaginated view.
      lifters.sort((a, b) => {
        if (a.platform !== b.platform) return a.platform - b.platform;
        if (a.flight !== b.flight) return a.flight < b.flight ? -1 : 1;
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });

      orderedEntries.push(...lifters);
    }

    return orderedEntries;
  };

  handleFiltersChange(filters: WeighinsFiltersState) {
    const statusSnapshot = hasStatusFilter(filters)
      ? new Set(this.props.entries.filter((e) => matchesWeighinsStatus(e, filters)).map((e) => e.id))
      : null;
    this.setState({ filters, statusSnapshot, currentPage: 0 });
  }

  handleFiltersClear() {
    this.setState({ filters: emptyWeighinsFilters(), statusSnapshot: null, currentPage: 0 });
  }

  // "Pesados: X de Y · Ausentes: Z" for a day, over all of its lifters (not just the visible ones).
  getDayCounts = (day: number): string => {
    const lifters = getLiftersOnDay(this.props.entries, day);
    const weighed = lifters.filter((e) => e.weighedIn === true).length;
    const absent = lifters.filter((e) => !isEntryPresent(e)).length;
    return getString("weigh-ins.day-counts", this.props.language)
      .replace("{weighed}", String(weighed))
      .replace("{total}", String(lifters.length))
      .replace("{absent}", String(absent));
  };

  handlePreviousPage() {
    this.setState((state) => ({ currentPage: Math.max(state.currentPage - 1, 0) }));
  }

  handleNextPage() {
    this.setState((state) => ({ currentPage: state.currentPage + 1 }));
  }

  componentDidUpdate(prevProps: Props) {
    const scopeChanged = prevProps.day !== this.props.day || prevProps.platform !== this.props.platform;
    const entriesChanged = prevProps.entries !== this.props.entries;
    const paginationSettingChanged = prevProps.paginationEnabled !== this.props.paginationEnabled;
    if (!scopeChanged && !entriesChanged && !paginationSettingChanged) {
      return;
    }

    // The frozen status filter belongs to the previous day/platform: take it again for the new one.
    if (scopeChanged) {
      this.handleFiltersChange(this.state.filters);
      return;
    }

    if (!this.props.paginationEnabled) {
      if (this.state.currentPage !== 0) {
        this.setState({ currentPage: 0 });
      }
      return;
    }

    const pagination = paginateEntries(this.getOrderedEntries(), this.state.currentPage);
    if (pagination.page !== this.state.currentPage) {
      this.setState({ currentPage: pagination.page });
    }
  }

  render() {
    // Determine whether this is being shown embedded in the Lifting page.
    const inLiftingPage = this.props.inLiftingPage === true;

    const orderedEntries = this.getOrderedEntries();
    const pagination = this.props.paginationEnabled
      ? paginateEntries(orderedEntries, this.state.currentPage)
      : { page: 0, totalPages: 0, entries: orderedEntries };
    const paginatedEntriesByDay = new Map<number, Entry[]>();
    for (const entry of pagination.entries) {
      const dayEntries = paginatedEntriesByDay.get(entry.day);
      if (dayEntries === undefined) {
        paginatedEntriesByDay.set(entry.day, [entry]);
      } else {
        dayEntries.push(entry);
      }
    }

    // Make a separate panel for each day represented on this page.
    const dayCards = [];
    for (const [day, lifters] of paginatedEntriesByDay) {
      dayCards.push(
        <Card key={day} style={{ marginBottom: "17px" }}>
          <Card.Header>
            <FormattedMessage
              id="weigh-ins.day-header"
              defaultMessage="Day {dayNumber} Weigh-ins"
              values={{ dayNumber: day }}
            />
            <span className="text-muted" style={{ marginLeft: "12px", fontWeight: "normal" }}>
              {this.getDayCounts(day)}
            </span>
          </Card.Header>
          <Card.Body>
            <LifterTable entries={lifters} rowRenderer={LifterRow} inLiftingPage={inLiftingPage} />
          </Card.Body>
        </Card>,
      );
    }

    if (pagination.totalPages > 1) {
      dayCards.unshift(
        <div key="pagination" style={{ display: "flex", justifyContent: "center", marginBottom: "17px" }}>
          <ButtonGroup aria-label="Weigh-ins pagination">
            <Button variant="secondary" onClick={this.handlePreviousPage} disabled={pagination.page === 0}>
              <FormattedMessage id="weigh-ins.pagination-previous" defaultMessage="Previous" />
            </Button>
            <Button variant="light" disabled>
              <FormattedMessage
                id="weigh-ins.pagination-page"
                defaultMessage="Page {page} of {total}"
                values={{ page: pagination.page + 1, total: pagination.totalPages }}
              />
            </Button>
            <Button
              variant="secondary"
              onClick={this.handleNextPage}
              disabled={pagination.page >= pagination.totalPages - 1}
            >
              <FormattedMessage id="weigh-ins.pagination-next" defaultMessage="Next" />
            </Button>
          </ButtonGroup>
        </div>,
      );
    }

    // Lifters exist but none match the search/filters.
    if (dayCards.length === 0 && this.props.entries.length > 0) {
      dayCards.push(
        <Card key={0}>
          <Card.Body>{getString("weigh-ins.filter-no-match", this.props.language)}</Card.Body>
        </Card>,
      );
    }

    // If there are no days thus far, show a default warning panel.
    if (dayCards.length === 0) {
      dayCards.push(
        <Card key={0}>
          <Card.Header>
            <FormattedMessage id="weigh-ins.empty-header" defaultMessage="Waiting for Registration" />
          </Card.Header>
          <Card.Body>
            <FormattedMessage
              id="weigh-ins.empty-body"
              defaultMessage="Add lifters on the Registration page before weighing them in."
            />
          </Card.Body>
        </Card>,
      );
    }

    return (
      <div>
        {/* Pesajes cargados en la web (otra PC o celular) y tandas del wizard, con vista previa. */}
        {!inLiftingPage && <WrpfPullFromWebButton />}
        {this.props.entries.length > 0 && (
          <WeighinsFilters
            filters={this.state.filters}
            language={this.props.language}
            visibleCount={orderedEntries.length}
            totalCount={this.props.entries.length}
            onChange={this.handleFiltersChange}
            onClear={this.handleFiltersClear}
          />
        )}
        {dayCards}
      </div>
    );
  }
}

const mapStateToProps = (state: GlobalState, ownProps: OwnProps): StateProps => {
  const { day, platform } = ownProps;
  let entries = state.registration.entries;

  // Filter if requested by the OwnProps.
  if (typeof day === "number" && typeof platform === "number") {
    entries = entries.filter((e) => e.day === day && e.platform === platform);
  }

  return {
    entries,
    paginationEnabled: state.wrpfLive.developerSettings.registrationPaginationEnabled,
    language: state.language,
  };
};

export default connect(mapStateToProps)(WeighinsView);
